// The table's felt: the market's price running live toward a gold line that pays and a red line
// that busts. A canvas redrawn every frame from the marks this page has seen, with Hyperliquid's
// 1m closes behind them for the minutes before it opened. Floats: pixels, never money.

import { useEffect, useRef } from "react";
import { since, useHistory } from "../../hl/history";
import { ticksOf } from "../../play/ticks";

export type Level = {
  /** Raw at the market's scale, as a number. */
  at: number;
  kind: "win" | "bust";
  label: string;
  /** In full, or faint behind the bet in focus. */
  strong: boolean;
  /** A bet not placed yet. */
  preview: boolean;
};

export type Entry = { t: number; at: number; strong: boolean };

export type Scene = {
  instrumentId: number;
  coin: string;
  /** Raw units per whole price: 10 to the market's places. */
  unit: number;
  levels: Level[];
  entries: Entry[];
};

const WINDOW_MS = 4 * 60_000;
const MINUTE = 60_000;

const INK = "246, 242, 234";
const GOLD = "240, 198, 106";
const RED = "255, 77, 97";

/** Chases `to` from `from` at `rate` per second, frame-rate independent. */
const chase = (from: number, to: number, rate: number, dt: number) => from + (to - from) * (1 - Math.exp(-rate * dt));

function pill(ctx: CanvasRenderingContext2D, text: string, right: number, y: number, fill: string, ink: string) {
  ctx.font = "600 12px Inter, system-ui, sans-serif";
  const width = Math.ceil(ctx.measureText(text).width) + 16;
  const height = 22;
  const left = right - width;
  const top = Math.round(y - height / 2);
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.roundRect(left, top, width, height, 4);
  ctx.fill();
  ctx.fillStyle = ink;
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  ctx.fillText(text, left + 8, top + height / 2 + 0.5);
}

export function Arena({ scene }: { scene: Scene }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const live = useRef(scene);
  live.current = scene;
  useHistory(scene.coin);

  useEffect(() => {
    const element = canvas.current;
    const ctx = element?.getContext("2d");
    if (!element || !ctx) return;

    let width = 0;
    let height = 0;
    let dpr = 1;
    const resize = () => {
      const box = element.getBoundingClientRect();
      dpr = Math.min(2, window.devicePixelRatio || 1);
      width = box.width;
      height = box.height;
      element.width = Math.max(1, Math.round(width * dpr));
      element.height = Math.max(1, Math.round(height * dpr));
    };
    const observer = new ResizeObserver(resize);
    observer.observe(element);
    resize();

    let shown: number | null = null;
    let market = -1;
    let lo = 0;
    let hi = 0;
    let last = performance.now();
    let frame = 0;

    const draw = (time: number) => {
      frame = requestAnimationFrame(draw);
      const dt = Math.min(0.1, (time - last) / 1000);
      last = time;
      const s = live.current;
      const now = Date.now();
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);
      if (width < 10 || height < 10) return;

      const narrow = width < 480;
      const nowX = width * 0.8;
      const top = 28;
      const bottom = height - 28;
      const x = (t: number) => nowX - ((now - t) / WINDOW_MS) * nowX;

      // the points: Hyperliquid's closes before this page's first mark, then every mark
      const start = now - WINDOW_MS - 20_000;
      const series = ticksOf(s.instrumentId);
      const first = series?.t[0] ?? now;
      const ts: number[] = [];
      const vs: number[] = [];
      if (first > start) {
        const back = since(s.coin, start - 2 * MINUTE);
        for (let i = 0; i < back.times.length; i += 1) {
          const t = (back.times[i] ?? 0) + MINUTE;
          // one close before the window, so the line comes in from the edge
          if (t < start - MINUTE || t >= first) continue;
          ts.push(t);
          vs.push((back.closes[i] ?? 0) * s.unit);
        }
      }
      if (series) {
        for (let i = 0; i < series.t.length; i += 1) {
          const t = series.t[i] ?? 0;
          if (t < start) continue;
          ts.push(t);
          vs.push(series.v[i] ?? 0);
        }
      }
      const latest = vs[vs.length - 1];
      if (latest === undefined) return;

      if (market !== s.instrumentId || shown === null) {
        market = s.instrumentId;
        shown = latest;
        lo = 0;
        hi = 0;
      }
      shown = chase(shown, latest, 14, dt);

      // the range: the price's last minutes and every line in full, with room to breathe
      let low = shown;
      let high = shown;
      for (const v of vs) {
        if (v < low) low = v;
        if (v > high) high = v;
      }
      for (const level of s.levels) {
        if (!level.strong) continue;
        if (level.at < low) low = level.at;
        if (level.at > high) high = level.at;
      }
      const span = Math.max(high - low, shown * 0.0006);
      const wantLo = low - span * 0.06;
      const wantHi = high + span * 0.06;
      if (lo === 0 && hi === 0) {
        lo = wantLo;
        hi = wantHi;
      } else {
        lo = chase(lo, wantLo, 5, dt);
        hi = chase(hi, wantHi, 5, dt);
      }
      const y = (v: number) => bottom - ((v - lo) / (hi - lo || 1)) * (bottom - top);

      // the minutes, faint on the felt
      ctx.strokeStyle = "rgba(232, 245, 236, 0.045)";
      ctx.lineWidth = 1;
      for (let m = Math.ceil(start / MINUTE) * MINUTE; m <= now; m += MINUTE) {
        const at = Math.round(x(m)) + 0.5;
        ctx.beginPath();
        ctx.moveTo(at, 0);
        ctx.lineTo(at, height);
        ctx.stroke();
      }
      // the zones past each line: gold pays, red busts
      const ordered = [...s.levels].sort((a, b) => Number(a.strong) - Number(b.strong));
      for (const level of ordered) {
        const ly = y(level.at);
        const above = level.at > shown;
        const rgb = level.kind === "win" ? GOLD : RED;
        const strength = level.strong ? (level.preview ? 0.1 : 0.16) : 0.04;
        const zone = ctx.createLinearGradient(0, ly, 0, above ? 0 : height);
        zone.addColorStop(0, `rgba(${rgb}, ${strength})`);
        zone.addColorStop(1, `rgba(${rgb}, 0)`);
        ctx.fillStyle = zone;
        if (above) ctx.fillRect(0, 0, width, ly);
        else ctx.fillRect(0, ly, width, height - ly);
      }

      // the felt takes the edges back: the grid and the zones fade out on every side
      ctx.save();
      ctx.globalCompositeOperation = "destination-in";
      const across = ctx.createLinearGradient(0, 0, width, 0);
      across.addColorStop(0, "rgba(0, 0, 0, 0)");
      across.addColorStop(0.18, "rgba(0, 0, 0, 1)");
      across.addColorStop(0.82, "rgba(0, 0, 0, 1)");
      across.addColorStop(1, "rgba(0, 0, 0, 0)");
      ctx.fillStyle = across;
      ctx.fillRect(0, 0, width, height);
      const down = ctx.createLinearGradient(0, 0, 0, height);
      down.addColorStop(0, "rgba(0, 0, 0, 0)");
      down.addColorStop(0.12, "rgba(0, 0, 0, 1)");
      down.addColorStop(0.88, "rgba(0, 0, 0, 1)");
      down.addColorStop(1, "rgba(0, 0, 0, 0)");
      ctx.fillStyle = down;
      ctx.fillRect(0, 0, width, height);
      ctx.restore();

      // the lines
      for (const level of ordered) {
        const ly = Math.round(y(level.at)) + 0.5;
        const rgb = level.kind === "win" ? GOLD : RED;
        ctx.save();
        ctx.strokeStyle = `rgba(${rgb}, ${level.strong ? 0.95 : 0.3})`;
        ctx.lineWidth = level.strong ? 1.5 : 1;
        if (level.preview) ctx.setLineDash([6, 6]);
        if (level.strong && !level.preview) {
          ctx.shadowColor = `rgba(${rgb}, 0.6)`;
          ctx.shadowBlur = 10;
        }
        ctx.beginPath();
        ctx.moveTo(0, ly);
        ctx.lineTo(width, ly);
        ctx.stroke();
        ctx.restore();
      }

      // where each bet came in
      for (const entry of s.entries) {
        const ex = x(entry.t);
        const ey = y(entry.at);
        ctx.save();
        ctx.strokeStyle = `rgba(${INK}, ${entry.strong ? 0.35 : 0.12})`;
        ctx.setLineDash([2, 4]);
        ctx.beginPath();
        ctx.moveTo(Math.max(0, ex), ey);
        ctx.lineTo(nowX, ey);
        ctx.stroke();
        ctx.restore();
        if (ex >= 0) {
          ctx.fillStyle = entry.strong ? `rgba(${GOLD}, 1)` : `rgba(${GOLD}, 0.4)`;
          ctx.beginPath();
          ctx.arc(ex, ey, entry.strong ? 4.5 : 3, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = "rgba(5, 21, 16, 0.9)";
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }

      // the price: one point per pixel, the latest at the now line, eased
      const px: number[] = [];
      const py: number[] = [];
      let lastPixel = -Infinity;
      for (let i = 0; i < ts.length; i += 1) {
        const at = x(ts[i] ?? 0);
        // the last few pixels belong to the eased ball, not the raw tick
        if (at >= nowX - 6) break;
        const value = vs[i] ?? shown;
        if (at - lastPixel < 1.5 && px.length) {
          py[py.length - 1] = y(value);
          continue;
        }
        px.push(at);
        py.push(y(value));
        lastPixel = at;
      }
      const nowY = y(shown);
      px.push(nowX);
      py.push(nowY);

      if (px.length > 1) {
        const trace = new Path2D();
        trace.moveTo(px[0] ?? 0, py[0] ?? 0);
        for (let i = 1; i < px.length - 1; i += 1) {
          const mx = ((px[i] ?? 0) + (px[i + 1] ?? 0)) / 2;
          const my = ((py[i] ?? 0) + (py[i + 1] ?? 0)) / 2;
          trace.quadraticCurveTo(px[i] ?? 0, py[i] ?? 0, mx, my);
        }
        trace.lineTo(nowX, nowY);

        const area = new Path2D(trace);
        area.lineTo(nowX, height);
        area.lineTo(px[0] ?? 0, height);
        area.closePath();
        const fill = ctx.createLinearGradient(0, top, 0, height);
        fill.addColorStop(0, `rgba(${INK}, 0.1)`);
        fill.addColorStop(1, `rgba(${INK}, 0)`);
        ctx.fillStyle = fill;
        ctx.fill(area);

        ctx.save();
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        ctx.strokeStyle = `rgba(${INK}, 0.98)`;
        ctx.lineWidth = 2;
        ctx.shadowColor = `rgba(${INK}, 0.45)`;
        ctx.shadowBlur = 12;
        ctx.stroke(trace);
        ctx.restore();
      }

      // the now line and the ball on it
      ctx.strokeStyle = `rgba(${INK}, 0.08)`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(Math.round(nowX) + 0.5, 0);
      ctx.lineTo(Math.round(nowX) + 0.5, height);
      ctx.stroke();
      const beat = (time % 1600) / 1600;
      ctx.fillStyle = `rgba(${INK}, ${0.35 * (1 - beat)})`;
      ctx.beginPath();
      ctx.arc(nowX, nowY, 5 + beat * 14, 0, Math.PI * 2);
      ctx.fill();
      ctx.save();
      ctx.fillStyle = `rgb(${INK})`;
      ctx.shadowColor = `rgba(${INK}, 0.9)`;
      ctx.shadowBlur = 14;
      ctx.beginPath();
      ctx.arc(nowX, nowY, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // close to a line, the room feels it
      const win = s.levels.find((level) => level.strong && level.kind === "win");
      const bust = s.levels.find((level) => level.strong && level.kind === "bust");
      if (win && bust && win.at !== bust.at) {
        const current = shown;
        const progress = (current - bust.at) / (win.at - bust.at);
        const near = (edge: number, rgb: string, at: number) => {
          if (edge <= 0) return;
          const toTop = at > current;
          const glow = ctx.createLinearGradient(0, toTop ? 0 : height, 0, toTop ? height * 0.45 : height * 0.55);
          glow.addColorStop(0, `rgba(${rgb}, ${0.32 * Math.min(1, edge)})`);
          glow.addColorStop(1, `rgba(${rgb}, 0)`);
          ctx.fillStyle = glow;
          ctx.fillRect(0, 0, width, height);
        };
        near((0.3 - progress) / 0.3, RED, bust.at);
        near((progress - 0.7) / 0.3, GOLD, win.at);
      }

      // the labels, over everything
      const right = width - (narrow ? 4 : 8);
      for (const level of s.levels) {
        if (!level.strong) continue;
        const ly = y(level.at);
        const gold = level.kind === "win";
        pill(ctx, level.label, right, ly, gold ? `rgb(${GOLD})` : `rgb(${RED})`, gold ? "#1c1405" : "#ffffff");
      }
    };
    frame = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  return <canvas ref={canvas} className="arena-canvas" aria-hidden />;
}
